import { useEffect } from "react";
import { navigate } from "@reach/router";
import { useSelector } from "react-redux";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { selectCurrentUserId } from "newui/features/Auth/authSelectors";
import { useHasAIEvaluationAccess } from "hooks/featureFlags";
import { useEvaluation } from "../../hooks/useEvaluation";
import { InitialEvaluationProvider } from "../../hooks/InitialEvaluationContext";
import { EVALUATION_STEP_NUMBERS } from "../../constants/evaluation.constants";
import { RfxLeadInfoBox } from "../steps/InitialEvaluationStep/panel/RfxLeadInfoBox";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import type { PanelMode } from "../../types/panel.type";
import { InvitationBanner } from "../steps/InitialEvaluationStep/panel";
import { EvaluationModeSelector } from "./EvaluationModeSelector";

interface EvaluationTabDisplayProps {
	isClientView: boolean;
	rfxData: Rfx;
	rfxSubmissionId?: number;
}

export const EvaluationTabDisplay: React.FC<EvaluationTabDisplayProps> = ({
	rfxData,
	rfxSubmissionId,
	isClientView,
}) => {
	const { clientPaths } = useAuthContext();
	const hasAIAccess = useHasAIEvaluationAccess();
	const currentUserId = useSelector(selectCurrentUserId);
	const rfxId = rfxData?.id;
	const rfxLead = rfxData?.rfxLead;

	const {
		hasStarted,
		isLoading,
		evaluationState,
		currentUserContext,
		fetchEvaluationState,
		startEvaluation,
		changeMode,
	} = useEvaluation({ rfxId, rfxSubmissionId });

	// Pre-creation: compare current user with rfxLead.id
	// Post-creation: use the backend-derived context
	const isRfxLead = evaluationState
		? (currentUserContext?.isRfxLead ?? false)
		: (currentUserId !== null && rfxLead?.id === currentUserId);

	useEffect(() => {
		fetchEvaluationState();
	}, [fetchEvaluationState]);

	const handleView = (): void => {
		const evaluationId = evaluationState?.id;
		if (evaluationId) {
			navigate(clientPaths.viewEvaluation(rfxId, evaluationId));
		}
	};

	const handleCreate = async (panelMode: PanelMode): Promise<void> => {
		const state = await startEvaluation(panelMode);
		const evaluationId = state?.id;
		if (evaluationId) {
			navigate(clientPaths.viewEvaluation(rfxId, evaluationId));
		}
	};

	// Compute whether mode switching is currently allowed
	const canChangeMode = (() => {
		if (!hasStarted || !evaluationState?.panelMode) return false;
		const mode = evaluationState.panelMode;
		const step = evaluationState.currentStep ?? 0;
		// Solo → Panel: allowed before Final Scoring (step number 5)
		if (mode === "manual_solo" || mode === "ai_solo") {
			return step < EVALUATION_STEP_NUMBERS.PANEL_MEETING;
		}
		// Panel → Solo: allowed before invites sent (for now, pass true; refine after testing)
		if (mode === "manual_panel" || mode === "ai_panel") {
			return true;
		}
		return false;
	})();

	const handleModeChange = async (newMode: PanelMode): Promise<void> => {
		await changeMode(newMode);
	};

	const handleAcceptPanelInvite = () => {
		navigate(clientPaths.viewEvaluation(rfxId, evaluationState?.id));
	};

	const handleDeclined = () => {
		fetchEvaluationState();
	};

	if (!isClientView) {
		return null;
	}

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
				<SkeletonBase
					startColor="lightgray"
					width="60%"
					height="40px"
					className="!rounded-lg"
				/>
			</div>
		);
	}

	return (
		<InitialEvaluationProvider rfxId={rfxId}>
			<div data-testid="evaluation-panel" className="min-h-96">
				<div className="mb-6">
					<h2 className="text-xl font-semibold text-gray-900">
						{hasAIAccess ? (
							<>EvaluA<span className="text-utility-brand-700">I</span>tion tool</>
						) : (
							"Evaluation"
						)}
					</h2>
					<p className="mt-1 text-sm text-gray-500">
						{hasAIAccess
							? "Use AI-assisted evaluation to systematically score and compare supplier submissions."
							: "Systematically score and compare supplier submissions."}
					</p>
				</div>
				{hasStarted && !isRfxLead && (
					<div className="mb-4">
						<InvitationBanner evalId={evaluationState?.id} memberId={currentUserContext?.panelMemberId} rfxData={rfxData}
							onAccept={handleAcceptPanelInvite} currentStatus={currentUserContext?.panelMemberStatus} currentStep={evaluationState?.currentStep} onDecline={handleDeclined} />
					</div>
				)}
				{isRfxLead ? (
					<div>
						<EvaluationModeSelector
							onStart={handleCreate}
							hasAIAccess={hasAIAccess}
							isLoading={isLoading}
							currentMode={evaluationState?.panelMode as PanelMode | undefined}
							currentStep={evaluationState?.currentStep}
							canChangeMode={canChangeMode}
							onModeChange={handleModeChange}
						/>
						{hasStarted && (
							<Button
								btntype="primary"
								onClick={handleView}
								data-testid="view-evaluation-btn"
							>
								View Evaluation
							</Button>
						)}
					</div>
				) : !hasStarted ? (
					<div className="space-y-3" data-testid="evaluation-not-started-info">
						<p className="text-md text-gray-600">
							The RFx Lead has not started the Evaluation Process yet.
						</p>
						{rfxLead && (
							<RfxLeadInfoBox
								fullName={rfxLead.fullName}
								email={rfxLead.email}
								phoneNumber={rfxLead.phoneNumber}
							/>
						)}
					</div>
				) : (
					<div>
						<EvaluationModeSelector
							onStart={handleCreate}
							hasAIAccess={hasAIAccess}
							isLoading={false}
							currentMode={evaluationState?.panelMode as PanelMode | undefined}
							currentStep={evaluationState?.currentStep}
							canChangeMode={false}
						/>
						<Button
							btntype="primary"
							onClick={handleView}
							data-testid="view-evaluation-btn"
						>
							View Evaluation
						</Button>
					</div>
				)}
			</div>
		</InitialEvaluationProvider>
	);
};
